/**
 * Settings Redux Slice
 * Manages site-wide settings fetched from Firestore
 */
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '@/store';
import type {
  AllSettings,
  GeneralSettings,
  HomeContent,
  AboutContent,
  ContactContent,
  HeaderSettings,
  FooterSettings,
} from '@/types/settings.types';
import {
  getGeneralSettings,
  getHomeContent,
  getAboutContent,
  getContactContent,
  getHeaderSettings,
  getFooterSettings,
} from '@/services/firebase/settings.service';

interface SettingsState extends AllSettings {
  loading: boolean;
  error: string | null;
}

const initialState: SettingsState = {
  general: null,
  home: null,
  about: null,
  contact: null,
  header: null,
  footer: null,
  loading: false,
  error: null,
};

export const fetchAllSettings = createAsyncThunk(
  'settings/fetchAll',
  async () => {
    const [general, home, about, contact, header, footer] = await Promise.all([
      getGeneralSettings(),
      getHomeContent(),
      getAboutContent(),
      getContactContent(),
      getHeaderSettings(),
      getFooterSettings(),
    ]);
    return { general, home, about, contact, header, footer };
  }
);

const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    setGeneral: (state, action: PayloadAction<GeneralSettings>) => {
      state.general = action.payload;
    },
    setHome: (state, action: PayloadAction<HomeContent>) => {
      state.home = action.payload;
    },
    setAbout: (state, action: PayloadAction<AboutContent>) => {
      state.about = action.payload;
    },
    setContact: (state, action: PayloadAction<ContactContent>) => {
      state.contact = action.payload;
    },
    setHeader: (state, action: PayloadAction<HeaderSettings>) => {
      state.header = action.payload;
    },
    setFooter: (state, action: PayloadAction<FooterSettings>) => {
      state.footer = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllSettings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllSettings.fulfilled, (state, action) => {
        state.loading = false;
        state.general = action.payload.general;
        state.home = action.payload.home;
        state.about = action.payload.about;
        state.contact = action.payload.contact;
        state.header = action.payload.header;
        state.footer = action.payload.footer;
      })
      .addCase(fetchAllSettings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch settings';
      });
  },
});

export const { setGeneral, setHome, setAbout, setContact, setHeader, setFooter } = settingsSlice.actions;
export default settingsSlice.reducer;

// Selectors
export const selectGeneralSettings = (state: RootState) => state.settings.general;
export const selectHomeContent = (state: RootState) => state.settings.home;
export const selectAboutContent = (state: RootState) => state.settings.about;
export const selectContactContent = (state: RootState) => state.settings.contact;
export const selectHeaderSettings = (state: RootState) => state.settings.header;
export const selectFooterSettings = (state: RootState) => state.settings.footer;
export const selectSettingsLoading = (state: RootState) => state.settings.loading;

export const selectStoreInfo = (state: RootState) => state.settings.general?.storeInfo ?? null;
export const selectBusinessRules = (state: RootState) => state.settings.general?.businessRules ?? null;
export const selectSocialLinks = (state: RootState) => state.settings.general?.socialLinks ?? null;
export const selectSeoSettings = (state: RootState) => state.settings.general?.seo ?? null;
export const selectNotifications = (state: RootState) => state.settings.general?.notifications ?? null;
